import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { compact, keyBy } from 'lodash-es';
import { pathgen } from 'object-path-generator';
import { BottomSheetContentLayout, Box, BulletList, Column, DraggableGrid, MobileSummaryCard, Row, TextButton, Typography, WithInfoIcon, useDevice, useIsInBottomSheet, useOverlayContext } from 'seagull';
import { generateUniqueId } from 'seagull/utils/generateUniqueId';
import EditServiceContentModal from '~/components/EditServiceContentModal/EditServiceContentModal';
import { useStore } from '~/contexts';
import { useActiveServiceTemplates } from '~/hooks/reactQuery/queries/useActiveServiceTemplates';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { serviceCostModel, serviceModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import { filterTemplates } from './AddServices/AddServices.utils';
import { AddServicesSearchbox } from './AddServices/AddServicesSearchbox';
import { AddServicesServiceList } from './AddServices/AddServicesServiceList';
import AddServicesFilterPicker from './AddServicesFilterPicker';
import styles from './IncludedServices.module.scss';
import ServiceItem from './ServiceItem/ServiceItem';
export function applyServicesSelection(currentServices, selectedServiceTemplateIds, serviceTemplatesMap) {
    const remainingIncludedServices = currentServices.filter((s)=>s.serviceTemplateId && selectedServiceTemplateIds.includes(s.serviceTemplateId));
    const newServiceTemplateIds = selectedServiceTemplateIds.filter((id)=>!remainingIncludedServices.some((s)=>s.serviceTemplateId === id));
    const newIncludedServices = compact(newServiceTemplateIds.map((serviceTemplateId)=>{
        const template = serviceTemplatesMap[serviceTemplateId];
        if (!template) return null;
        const includedService = {
            id: generateUniqueId('service-bundle'),
            serviceTemplateId,
            name: template.name,
            description: template.description,
            rate: serviceCostModel.finalPrice(template.cost, 1),
            integrations: template.integrations
        };
        return includedService;
    }));
    return remainingIncludedServices.concat(newIncludedServices);
}
export const IncludedServicesDataTestIds = pathgen('IncludedServicesDataTestIds');
const AddServicesBottomSheetContent = (param)=>{
    let { serviceTemplates, initialSelectedIds, onApply, hideBottomSheet, testId } = param;
    const [selectedIds, setSelectedIds] = useState(initialSelectedIds);
    const [searchTerm, setSearchTerm] = useState('');
    const filteredTemplates = useMemo(()=>filterTemplates(serviceTemplates, searchTerm), [
        serviceTemplates,
        searchTerm
    ]);
    function handleToggle(templateId) {
        setSelectedIds((prev)=>prev.includes(templateId) ? prev.filter((id)=>id !== templateId) : [
                ...prev,
                templateId
            ]);
    }
    function handleSave() {
        onApply(selectedIds);
        hideBottomSheet();
    }
    return /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
        header: {
            title: 'Add services',
            subtitle: /*#__PURE__*/ _jsx(AddServicesSearchbox, {
                searchTerm: searchTerm,
                onSearchChange: setSearchTerm,
                onSearchClear: ()=>setSearchTerm('')
            }),
            gutter: 'm'
        },
        contentGutter: "m",
        footer: {
            primaryAction: {
                text: 'Save',
                onClick: handleSave,
                testId: "".concat(testId, "-save")
            },
            paddingTop: 8
        },
        children: /*#__PURE__*/ _jsx(AddServicesServiceList, {
            serviceTemplates: filteredTemplates,
            selectedServiceTemplateIds: selectedIds,
            onToggle: handleToggle,
            noNegativeMargin: true
        })
    });
};
const IncludedServices = (props)=>{
    const { mode, value, showErrors = false, tooltip, onChange, disableMobileCard } = props;
    const isReadOnly = mode === 'readonly';
    const { isMobileDevice } = useDevice();
    const isInBottomSheet = useIsInBottomSheet();
    const shouldRenderAsMobileCard = isMobileDevice && !isInBottomSheet && !disableMobileCard;
    const { showModal, showBottomSheet, hideBottomSheet } = useOverlayContext();
    const { serviceTemplates } = useActiveServiceTemplates();
    const [selectedTemplateIds, setSelectedTemplateIds] = useState(getInitialSelectedTemplateIds);
    const isInvalid = serviceModel.validateBundleIncludedServices(value).length > 0;
    const serviceTemplatesMap = useMemo(()=>keyBy(serviceTemplates, 'id'), [
        serviceTemplates
    ]);
    const isVendor = useStore(selectors.user.isVendor);
    const { DRUGS_AND_DROPS: isDragAndDropEnabled = false } = useFeatureFlags();
    const sortedServiceTemplates = useMemo(()=>{
        const selectedServiceTemplates = serviceTemplates === null || serviceTemplates === void 0 ? void 0 : serviceTemplates.filter((serviceTemplate)=>value.some((service)=>service.serviceTemplateId === serviceTemplate.id));
        const unselectedServiceTemplates = serviceTemplates === null || serviceTemplates === void 0 ? void 0 : serviceTemplates.filter((serviceTemplate)=>!(selectedServiceTemplates === null || selectedServiceTemplates === void 0 ? void 0 : selectedServiceTemplates.includes(serviceTemplate)));
        return [
            ...selectedServiceTemplates || [],
            ...unselectedServiceTemplates || []
        ];
    }, [
        serviceTemplates,
        value
    ]);
    function handleApplyServices(selectedServiceTemplateIds) {
        const newServices = applyServicesSelection(value, selectedServiceTemplateIds, serviceTemplatesMap);
        setTimeout(()=>onChange(newServices), 150);
    }
    function handleDelete(includedService) {
        setSelectedTemplateIds((selectedServiceTemplateIds)=>selectedServiceTemplateIds.filter((serviceTemplateId)=>serviceTemplateId !== includedService.serviceTemplateId));
        onChange(value.filter((service)=>service.serviceTemplateId !== includedService.serviceTemplateId));
    }
    function handleEdit(includedService) {
        showModal({
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            openBottomSheetOnMobile: true,
            hideBottomSheetOnDismiss: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(EditServiceContentModal, {
                    width: "800px",
                    title: "Edit Included Service",
                    serviceName: includedService.name,
                    description: includedService.description,
                    serviceId: includedService.id,
                    dismissModal: dismissModal,
                    onSave: (param)=>{
                        let { name, description } = param;
                        onChange(value.map((service)=>service.serviceTemplateId === includedService.serviceTemplateId ? _object_spread_props(_object_spread({}, service), {
                                name,
                                description
                            }) : service));
                        dismissModal();
                    }
                });
            }
        });
    }
    function handleMobileApplyServices(selectedIds) {
        setSelectedTemplateIds(selectedIds);
        handleApplyServices(selectedIds);
    }
    function handleServiceToggle(templateId) {
        setSelectedTemplateIds((prev)=>prev.includes(templateId) ? prev.filter((id)=>id !== templateId) : [
                ...prev,
                templateId
            ]);
    }
    function getInitialSelectedTemplateIds() {
        return compact(value.map((service)=>service.serviceTemplateId));
    }
    const isDraggable = isDragAndDropEnabled && value.length >= 2 && !isReadOnly;
    function isServiceArchived(includedService) {
        return isVendor && !!serviceTemplates && !!includedService.serviceTemplateId && !serviceTemplatesMap[includedService.serviceTemplateId];
    }
    const mobileAddServicesButton = !isReadOnly && /*#__PURE__*/ _jsx(TextButton, {
        bi: {
            name: 'add_services'
        },
        type: "button",
        testId: "".concat(IncludedServicesDataTestIds.AddServices(), "-trigger"),
        underline: "none",
        skin: showErrors && isInvalid ? 'error' : 'primary',
        "aria-invalid": showErrors && isInvalid,
        onClick: ()=>showBottomSheet({
                content: /*#__PURE__*/ _jsx(AddServicesBottomSheetContent, {
                    serviceTemplates: sortedServiceTemplates,
                    initialSelectedIds: selectedTemplateIds,
                    onApply: handleMobileApplyServices,
                    hideBottomSheet: hideBottomSheet,
                    testId: IncludedServicesDataTestIds.AddServices()
                })
            }),
        children: "+ Add services"
    });
    const serviceItemEntries = value.map((includedService)=>({
            id: includedService.id,
            includedService,
            mode,
            isArchived: isServiceArchived(includedService),
            testId: IncludedServicesDataTestIds.ServiceItem(includedService.serviceTemplateId || ''),
            onDelete: handleDelete,
            onEdit: handleEdit
        }));
    function handleReorder(reorderedItems) {
        onChange(reorderedItems.map((item)=>item.includedService));
    }
    function renderServicesList() {
        let { transparentBackground } = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
        if (isDraggable) {
            const items = transparentBackground ? serviceItemEntries.map((entry)=>_object_spread_props(_object_spread({}, entry), {
                    transparentBackground
                })) : serviceItemEntries;
            return /*#__PURE__*/ _jsx(DraggableGrid, {
                items: items,
                itemComponent: ServiceItem,
                onReorder: handleReorder,
                dragMode: "solid",
                layout: {
                    columns: 1
                },
                itemHandleVisibility: "always",
                itemHandlePosition: "inside",
                itemDragActivation: "handle",
                testId: IncludedServicesDataTestIds.Root(),
                className: styles.draggableServicesWrapper
            });
        }
        return /*#__PURE__*/ _jsx(BulletList, {
            className: styles.servicesWrapper,
            children: serviceItemEntries.map((entry, index)=>/*#__PURE__*/ _jsx(ServiceItem, _object_spread_props(_object_spread({}, entry), {
                    transparentBackground: transparentBackground
                }), entry.includedService.serviceTemplateId || index))
        });
    }
    if (shouldRenderAsMobileCard) {
        const cardBody = /*#__PURE__*/ _jsxs(Column, {
            style: {
                gap: 8
            },
            children: [
                value.length > 0 && renderServicesList({
                    transparentBackground: true
                }),
                !isReadOnly && /*#__PURE__*/ _jsx(Box, {
                    className: styles.addServices,
                    children: mobileAddServicesButton
                })
            ]
        });
        return /*#__PURE__*/ _jsx(Column, {
            testId: IncludedServicesDataTestIds.Root(),
            children: /*#__PURE__*/ _jsx(MobileSummaryCard, {
                testId: IncludedServicesDataTestIds.MobileCard(),
                title: "Included Services",
                items: [
                    {
                        body: cardBody,
                        hideSeparator: true
                    }
                ]
            })
        });
    }
    return /*#__PURE__*/ _jsxs(Column, {
        testId: IncludedServicesDataTestIds.Root(),
        className: styles.root,
        children: [
            /*#__PURE__*/ _jsx(Row, {
                className: styles.includedServicesHeader,
                testId: IncludedServicesDataTestIds.IncludedServicesHeader(),
                children: /*#__PURE__*/ _jsx(WithInfoIcon, {
                    info: tooltip,
                    showInfo: !isReadOnly,
                    children: /*#__PURE__*/ _jsxs(Typography, {
                        weight: "normal",
                        children: [
                            "Included Services",
                            isReadOnly ? ':' : ''
                        ]
                    })
                })
            }),
            value.length > 0 && renderServicesList(),
            !isReadOnly && /*#__PURE__*/ _jsx(Box, {
                className: styles.addServices,
                children: isMobileDevice ? mobileAddServicesButton : /*#__PURE__*/ _jsx(AddServicesFilterPicker, {
                    hasError: showErrors && isInvalid,
                    serviceTemplates: sortedServiceTemplates,
                    selectedServiceTemplateIds: selectedTemplateIds,
                    testId: IncludedServicesDataTestIds.AddServices(),
                    onApply: handleApplyServices,
                    onToggle: handleServiceToggle,
                    onClose: (param)=>{
                        let { revert } = param;
                        if (revert) {
                            setSelectedTemplateIds(getInitialSelectedTemplateIds());
                        }
                    }
                })
            })
        ]
    });
};
export default IncludedServices;
